import { type EditUploadContentType } from '../types';
import {
  type CanvasVideoUploadRejectionReason,
  getVideoUploadRejectionReason,
  isVideoOverSizeLimit,
} from './canvasVideoUploadLimits';
import { getLocalVideoMetadata } from './getLocalVideoMetadata';
import {
  type AcceptedDroppedUploadFile,
  isImageEditUploadContentType,
  isVideoEditUploadContentType,
  type VideoEditUploadContentType,
} from './uploadMimeTypes';

export type UploadableImageFile = {
  file: File;
  contentType: Exclude<EditUploadContentType, VideoEditUploadContentType>;
};

export type UploadableVideoFile = {
  blobUrl: string;
  file: File;
  contentType: VideoEditUploadContentType;
  dimensions: { height: number; width: number };
  duration: number;
};

export type ClassifiedUploadFile =
  | (UploadableImageFile & { mediaKind: 'image' })
  | (UploadableVideoFile & { mediaKind: 'video' });

export type ClassifiedUploadFiles = {
  uploadFiles: ClassifiedUploadFile[];
  videoRejections: CanvasVideoUploadRejectionReason[];
};

/** Splits accepted OS upload files into uploadable images/videos and video rejection reasons. */
export const classifyAcceptedUploadFiles = async (
  accepted: AcceptedDroppedUploadFile[],
): Promise<ClassifiedUploadFiles> => {
  const uploadFiles: ClassifiedUploadFile[] = [];
  const videoRejections: CanvasVideoUploadRejectionReason[] = [];

  for (const { file, contentType } of accepted) {
    if (isVideoEditUploadContentType(contentType)) {
      if (isVideoOverSizeLimit(file.size)) {
        videoRejections.push('size');
        continue;
      }

      const metadata = await getLocalVideoMetadata(file);
      const rejection = getVideoUploadRejectionReason({
        durationSeconds: metadata?.duration,
        sizeBytes: file.size,
      });

      if (rejection) {
        videoRejections.push(rejection);
        if (metadata?.blobUrl) {
          URL.revokeObjectURL(metadata.blobUrl);
        }
      } else if (!metadata) {
        videoRejections.push('unknown');
      } else {
        uploadFiles.push({
          blobUrl: metadata.blobUrl,
          contentType,
          dimensions: { height: metadata.height, width: metadata.width },
          duration: metadata.duration,
          file,
          mediaKind: 'video',
        });
      }
    } else if (isImageEditUploadContentType(contentType)) {
      uploadFiles.push({ contentType, file, mediaKind: 'image' });
    }
  }

  return { uploadFiles, videoRejections };
};
